import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import AnimatedBackground from "@/components/AnimatedBackground";
import SEO from "@/components/SEO";
import TableOfContents from "@/components/TableOfContents";
import { useTranslation } from "react-i18next";
import {
  organizationSchema,
  websiteSchema,
  softwareApplicationSchema,
  faqSchema,
} from "@/lib/structured-data";

// Lazy-load below-the-fold sections to improve LCP
const ChallengesSection = lazy(() => import("@/components/ChallengesSection"));
const PlatformShowcaseSection = lazy(() => import("@/components/PlatformShowcaseSection"));
const XploraSection = lazy(() => import("@/components/XploraSection"));
const SolutionsSection = lazy(() => import("@/components/SolutionsSection"));
const HowItWorksSection = lazy(() => import("@/components/HowItWorksSection"));
const CapabilitiesSection = lazy(() => import("@/components/CapabilitiesSection"));
const CriticalEnvSection = lazy(() => import("@/components/CriticalEnvSection"));
const EnterprisePlatformSection = lazy(() => import("@/components/EnterprisePlatformSection"));
const UseCasesSection = lazy(() => import("@/components/UseCasesSection"));
const BenefitsSection = lazy(() => import("@/components/BenefitsSection"));
const WhyQualitaSection = lazy(() => import("@/components/WhyQualitaSection"));
const RegionalPresenceSection = lazy(() => import("@/components/RegionalPresenceSection"));
const BrochureSection = lazy(() => import("@/components/BrochureSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const CTASection = lazy(() => import("@/components/CTASection"));
const Footer = lazy(() => import("@/components/Footer"));

const Index = () => {
  const { t } = useTranslation();
  const faqItems = t("faq.items", { returnObjects: true }) as { q: string; a: string }[];
  const faqList = Array.isArray(faqItems) ? faqItems : [];

  return (
    <div className="min-h-screen relative">
      <SEO
        title={`Qualita Corp SAS | ${t("hero.badge")}`}
        description={t("hero.subtitle")}
        jsonLd={[
          organizationSchema(),
          websiteSchema(),
          softwareApplicationSchema(),
          faqSchema(faqList.map((f) => ({ question: f.q, answer: f.a }))),
        ]}
      />
      <AnimatedBackground />
      <Header />
      <TableOfContents />
      <HeroSection />
      <Suspense fallback={<div className="min-h-[400px]" />}>
        <div id="challenges" style={{ scrollMarginTop: 80 }}><ChallengesSection /></div>
        <div id="platform-showcase" style={{ scrollMarginTop: 80 }}><PlatformShowcaseSection /></div>
        <div id="xplora" style={{ scrollMarginTop: 80 }}><XploraSection /></div>
        <div id="solutions" style={{ scrollMarginTop: 80 }}><SolutionsSection /></div>
        <div id="how-it-works" style={{ scrollMarginTop: 80 }}><HowItWorksSection /></div>
        <div id="capabilities" style={{ scrollMarginTop: 80 }}><CapabilitiesSection /></div>
        <div id="critical-env" style={{ scrollMarginTop: 80 }}><CriticalEnvSection /></div>
        <div id="enterprise" style={{ scrollMarginTop: 80 }}><EnterprisePlatformSection /></div>
        <div id="use-cases" style={{ scrollMarginTop: 80 }}><UseCasesSection /></div>
        <div id="benefits" style={{ scrollMarginTop: 80 }}><BenefitsSection /></div>
        <div id="why-qualita" style={{ scrollMarginTop: 80 }}><WhyQualitaSection /></div>
        <div id="regional" style={{ scrollMarginTop: 80 }}><RegionalPresenceSection /></div>
        <div id="brochure" style={{ scrollMarginTop: 80 }}><BrochureSection /></div>
        <div id="faq" style={{ scrollMarginTop: 80 }}><FAQSection /></div>
        <div id="cta" style={{ scrollMarginTop: 80 }}><CTASection /></div>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
